import { useEffect, useMemo, useRef, useState } from "react";

import styles from "../styles/localization.module.scss";

import L from "leaflet";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { Title } from "../shared/components/Title";
import { Input } from "../shared/components/Input";
import { Checkbox } from "../shared/components/Checkbox";
import { Button } from "../shared/components/Button";
import SearchAddresses from "../shared/containers/SearchAddresses";
import { EnvironmentsAPI } from "../providers/EnvironmentsAPI";
import { EnvironmentStorage } from "../shared/tools/EnvironmentStorage";
import { Loading } from "../shared/components/Loading";
import { isDesktop, isMobile } from "react-device-detect";
import SearchAddressesLocation from "shared/containers/SearchAddressLocation";


const DraggableMarker = ({ position, readOnly, onChangePosition }) => {
	const icon = new L.Icon({
		iconUrl: require("../assets/location.png"),
		iconAnchor: [25, 50],
	});

	const [latLng, setPosition] = useState(position);

	const markerRef = useRef(null)
	const eventHandlers = useMemo(
		() => ({
			dragend() {
				const marker = markerRef.current
				if (marker != null) {
					setPosition(marker.getLatLng())
					onChangePosition && onChangePosition(marker.getLatLng())
				}
			},
		}),
		[],
	)

	return (
		<Marker
			draggable={!readOnly}
			eventHandlers={eventHandlers}
			position={latLng}
			ref={markerRef}
			icon={icon}
		/>
	)
}

export default function Localization() {

	const [isLoading, setIsLoading] = useState(false);
	const [updateLocalization, setUpdateLocalization] = useState(true);
	const [selectedAddress, setSelectedAddress] = useState(false);
	const [addressOptions, setAddressOptions] = useState()
	const [deliveryTime, setDeliveryTime] = useState({});
	const [environment, setEnvironment] = useState({});

	useEffect(() => {
		setEnvironment(EnvironmentStorage.getEnvironment())
		const environment_id = EnvironmentStorage.getSelectedEnvironment()
		EnvironmentsAPI.getConfigurationsAddress(environment_id).then((response) => {
			const address = response.getData({})
			setAddressOptions(address)
			if (address.address) {
				setUpdateLocalization(false)
				setSelectedAddress(true)
			}
		})
		EnvironmentsAPI.getConfigurationsDeliveryTime(environment_id).then((response) => {
			const deliveryTimeData = response.getData({})

			const allowDelivery = deliveryTimeData?.allowDelivery;

			setDeliveryTime({
				...deliveryTimeData,
				allowDelivery: allowDelivery === undefined ? true : allowDelivery,
			})
		})
	}, [])

	const handleAllowLocation = async (type) => {
		if (!deliveryTime) {
			setDeliveryTime({
				minDesk: "",
				maxDesk: "",
				[type]: false,
			})
		} else {
			setDeliveryTime({
				...deliveryTime,
				[type]: !deliveryTime[type]
			});
		}
	};

	const formattedAddress = () => {
		return `${addressOptions.address}${addressOptions.number ? `, ${addressOptions.number}` : ''}${addressOptions.district ? ` - ${addressOptions.district},` : ','
			} ${addressOptions.city} -
        ${addressOptions.state}, ${addressOptions.country} ${addressOptions.zipcode ? `- ${addressOptions.zipcode}` : ''
			}`;
	}

	const saveConfiguration = async () => {
		setIsLoading(true)
		const environment_id = EnvironmentStorage.getSelectedEnvironment()
		await EnvironmentsAPI.putConfigurationsAddressByEnvironmentId(environment_id, addressOptions)
		await EnvironmentsAPI.putConfigurationsDeliveryTimeByEnvironmentId(environment_id, deliveryTime)
		alert({ text: 'Salvo com sucesso!' })
		setIsLoading(false)
	}

	const getSubtitle = () => {
		if (environment?.brand)
			return `${environment.brand.name} - ${environment.name}` + (environment.active === false ? ' (Inativa)' : '');
		return environment?.name;
	};

	return (
		<div className={styles.localizationContainer}>
			<Title
				title="Localização"
				subtitle={getSubtitle()}
				backTo={{ url: "/configuracoes", name: "Configurações" }}
			/>
			{updateLocalization && (
				<>
					<div className={styles.addressContainer}>
						<SearchAddressesLocation setSelectedAddress={setAddressOptions} />
					</div>

					{addressOptions && addressOptions.address &&
						<div className={styles.mapContainer}>
							<div className={styles.map}>
								<MapContainer
									style={{ width: "100%", height: "100%" }}
									center={[addressOptions.geolocation.latitude, addressOptions.geolocation.longitude]}
									zoom={15}
									minZoom={15}
									maxZoom={18}
									attributionControl={false}
									zoomControl={true}
									doubleClickZoom={true}
									scrollWheelZoom={true}
								>
									<TileLayer
										url="https://mt0.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&apistyle=s.e%3Ag%7Cp.c%3A%23ff242f3e%2Cs.e%3Al.t.f%7Cp.c%3A%23ff746855%2Cs.e%3Al.t.s%7Cp.c%3A%23ff242f3e%2Cs.t%3A19%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A2%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A40%7Cs.e%3Ag%7Cp.c%3A%23ff263c3f%2Cs.t%3A40%7Cs.e%3Al.t.f%7Cp.c%3A%23ff6b9a76%2Cs.t%3A3%7Cs.e%3Ag%7Cp.c%3A%23ff38414e%2Cs.t%3A3%7Cs.e%3Ag.s%7Cp.c%3A%23ff212a37%2Cs.t%3A3%7Cs.e%3Al.t.f%7Cp.c%3A%23ff9ca5b3%2Cs.t%3A49%7Cs.e%3Ag%7Cp.c%3A%23ff746855%2Cs.t%3A49%7Cs.e%3Ag.s%7Cp.c%3A%23ff1f2835%2Cs.t%3A49%7Cs.e%3Al.t.f%7Cp.c%3A%23fff3d19c%2Cs.t%3A4%7Cs.e%3Ag%7Cp.c%3A%23ff2f3948%2Cs.t%3A66%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A6%7Cs.e%3Ag%7Cp.c%3A%23ff17263c%2Cs.t%3A6%7Cs.e%3Al.t.f%7Cp.c%3A%23ff515c6d%2Cs.t%3A6%7Cs.e%3Al.t.s%7Cp.c%3A%23ff17263c"
										attribution=""
									/>
									<DraggableMarker
										position={[addressOptions.geolocation.latitude, addressOptions.geolocation.longitude]}
										onChangePosition={(position) => {
											setAddressOptions({
												...addressOptions,
												geolocation: {
													...addressOptions.geolocation,
													latitude: position.lat,
													longitude: position.lng
												}
											})
										}}
									/>
								</MapContainer>
							</div>

							<Button
								className="w-100"
								design="primary"
								onClick={() => {
									setSelectedAddress(true);
									setUpdateLocalization(false);
								}}
							>
								Confirmar
							</Button>
						</div>
					}
				</>
			)}
			{isLoading && <Loading fullContent />}
			{!isLoading && selectedAddress && !updateLocalization && (
				<div className={styles.addressDetailsContainer}>
					<div className={styles.map}>
						<MapContainer
							style={{ width: "100%", height: "100%" }}
							center={[addressOptions.geolocation.latitude, addressOptions.geolocation.longitude]}
							zoom={15}
							minZoom={15}
							maxZoom={18}
							attributionControl={false}
							zoomControl={false}
							doubleClickZoom={false}
							scrollWheelZoom={false}
						>
							<TileLayer
								url="https://mt0.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&apistyle=s.e%3Ag%7Cp.c%3A%23ff242f3e%2Cs.e%3Al.t.f%7Cp.c%3A%23ff746855%2Cs.e%3Al.t.s%7Cp.c%3A%23ff242f3e%2Cs.t%3A19%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A2%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A40%7Cs.e%3Ag%7Cp.c%3A%23ff263c3f%2Cs.t%3A40%7Cs.e%3Al.t.f%7Cp.c%3A%23ff6b9a76%2Cs.t%3A3%7Cs.e%3Ag%7Cp.c%3A%23ff38414e%2Cs.t%3A3%7Cs.e%3Ag.s%7Cp.c%3A%23ff212a37%2Cs.t%3A3%7Cs.e%3Al.t.f%7Cp.c%3A%23ff9ca5b3%2Cs.t%3A49%7Cs.e%3Ag%7Cp.c%3A%23ff746855%2Cs.t%3A49%7Cs.e%3Ag.s%7Cp.c%3A%23ff1f2835%2Cs.t%3A49%7Cs.e%3Al.t.f%7Cp.c%3A%23fff3d19c%2Cs.t%3A4%7Cs.e%3Ag%7Cp.c%3A%23ff2f3948%2Cs.t%3A66%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A6%7Cs.e%3Ag%7Cp.c%3A%23ff17263c%2Cs.t%3A6%7Cs.e%3Al.t.f%7Cp.c%3A%23ff515c6d%2Cs.t%3A6%7Cs.e%3Al.t.s%7Cp.c%3A%23ff17263c"
								attribution=""
							/>
							<DraggableMarker
								position={[addressOptions.geolocation.latitude, addressOptions.geolocation.longitude]}
								readOnly
							/>
						</MapContainer>
					</div>

					<div className={styles.addressDetails}>
						<div className="d-flex align-center justify-between mb-10">
							<p>Endereço da sua loja</p>
							<Button
								design="underline"
								onClick={() => {
									setSelectedAddress(false);
									setUpdateLocalization(true);
									setAddressOptions();
								}}
							>
								EDITAR
							</Button>
						</div>
						<span className={styles.address}>
							{formattedAddress()}
						</span>
						<Input
							name="complement"
							design="three"
							floatLabel="Complemento"
							className="mb-20"
							value={addressOptions.complement || ''}
							onChange={(e) => setAddressOptions({ ...addressOptions, complement: e.target.value })}
						/>
						<Input
							name="referencePoint"
							design="three"
							label="Ponto de referência"
							placeholder="Ex.: Próximo a padaria"
							className="mb-15"
							value={addressOptions.observation || ''}
							onChange={(e) => setAddressOptions({ ...addressOptions, observation: e.target.value })}
						/>
						<Checkbox
							name="allowWithdrawal"
							design="two"
							className="mb-15"
							checked={
								Object.keys(deliveryTime || {}) && deliveryTime.allowLocation
							}
							onChange={() => handleAllowLocation("allowLocation")}
						>
							Permitir Para viagem
						</Checkbox>
						<Checkbox
							name="allowFastfila"
							design="two"
							className="mb-15"
							checked={
								Object.keys(deliveryTime || {}) && deliveryTime.allowFastfila
							}
							onChange={() => handleAllowLocation("allowFastfila")}
						>
							Permitir Consumo no local
						</Checkbox>
						<Checkbox
							name="allowDelivery"
							design="two"
							className="mb-15"
							checked={
								Object.keys(deliveryTime || {}) && deliveryTime.allowDelivery
							}
							onChange={() => handleAllowLocation("allowDelivery")}
						>
							Permitir Entrega
						</Checkbox>
						<div className={styles.column}>
							<Input
								name="complement"
								design="three"
								floatLabel="Tempo mínimo para retirada"
								disabled={!deliveryTime.allowLocation}
								value={deliveryTime.minDesk || ""}
								onChange={(e) => setDeliveryTime({ ...deliveryTime, minDesk: e.target.value })}
							/>
							<Input
								name="complement"
								design="three"
								floatLabel="Tempo máximo para retirada"
								disabled={!deliveryTime.allowLocation}
								value={deliveryTime.maxDesk || ""}
								onChange={(e) => setDeliveryTime({ ...deliveryTime, maxDesk: e.target.value })}
							/>
						</div>
						{isDesktop && (
							<Button
								className="w-100 mt-20"
								design="primary"
								onClick={saveConfiguration}
							>
								Salvar
							</Button>
						)}
					</div>

					{isMobile && (
						<div className={styles.save}>
							<Button design="primary" onClick={saveConfiguration} className="w-100">
								Salvar
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
